import React, { useEffect, useState } from 'react';
import qs from 'qs';
import {
  CandidateOpportunitiesConst,
  EventsConst,
  SubmissionCandidateNegativeStatuses,
} from '@axiom/const';
import type { ApiError } from '@axiom/ui';
import {
  CondensedHeader,
  CondensedLarge,
  ContextMenu,
  ContextMenuDivider,
  ContextMenuHeader,
  ContextMenuItem,
  Gutter,
  IconButton,
  ClipboardUtil,
  ToastUtil,
  useApi,
} from '@axiom/ui';
import {
  CandidateOpportunityUtils,
  DEFAULT_ALLOWED_TRANSITIONS,
  candidateOpportunityStatusUtils,
} from '@axiom/utils';
import type {
  Candidate,
  CandidateOpportunity,
  Opportunity,
  OpportunityCandidate,
  OpportunityPosition,
  CandidateRedux,
  OpportunityCandidateOrMlRecommended,
} from '@axiom/validation';
import { useNavigate } from 'react-router';

import FormStateTreatment from '../FormStateTreatment/FormStateTreatment';
import { CandidateOpportunityLegacyApi } from '../../api/candidate-opportunity-legacy-api';
import { OpportunityLegacyApi } from '../../api/opportunity-legacy-api';
import { CandidateApi } from '../../api/candidate-api';
import { CandidateAvailabilityModalStore } from '../../stores/candidate-availability-modal-store';
import { UserApi } from '../../api/user-api';
import { AppEventItemModalStore } from '../../stores/app-event-item-modal-store';
import { EventSubjectConst } from '../../stores/events-store';
import { AxiomForTalentUtil } from '../../utils/axiom-for-talent-util';
import { EnvUtil } from '../../utils/env-util';
import { PositionUtil } from '../../utils/position-util';
import { FormAutoCoolingModalStore } from '../../stores/form-auto-cooling-modal-store';
import { PreloadedAppErrorsStore } from '../../stores/preloaded-app-errors-store';
import { AutoCoolingModal } from '../AutoCoolingModal/AutoCoolingModal';
import { CandidateCalendarMenuContent } from '../CandidateCalendarMenuContent/CandidateCalendarMenuContent';
import type { TalentInterviewAvailabilityModalProps } from '../TalentInterviewAvailabilityModal/TalentInterviewAvailabilityModal';
import { TalentInterviewAvailabilityModal } from '../TalentInterviewAvailabilityModal/TalentInterviewAvailabilityModal';
import { isSalesUser } from '../../utils/roles-util';
import { OpportunityStore } from '../../stores/opportunity-store';
import { RejectionModal } from '../RejectionModal/RejectionModal';
import { AccountBenchApi } from '../../api/account-bench-api';
import { AddTalentUtil } from '../../utils/AddTalentUtil';
import { useInterviewAvailabilityCandidateData } from '../../hooks/useInterviewAvailabilityCandidateData';

import {
  poolContextMenuStatusLabels,
  positionContextMenuStatusLabels,
} from './opportunity-talent.const';
import { OpportunityTalentReassignPositionModal } from './OpportunityTalentReassignPositionModal';
import { OpportunityTalentWarmingModal } from './OpportunityTalentWarmingModal';
import { OpportunityTalentUtil } from './opportunity-talent-util';
import { OpportunityTalentMenuUtil } from './OpportunityTalentMenuUtil';

const { CandidateStatuses, MLRecommendationStatuses } =
  CandidateOpportunitiesConst;

const PREVIEW_TRANSITIONS: Partial<
  Record<
    NonNullable<CandidateOpportunity['candidateStatus']>,
    Array<CandidateOpportunity['candidateStatus']>
  >
> = {
  Cooled: DEFAULT_ALLOWED_TRANSITIONS.Cooled,
};

export type OpportunityTalentCandidateContextMenuProps = {
  name?: string;
  candidate: CandidateRedux | Candidate;
  currentCandidateStatus: CandidateOpportunity['candidateStatus'];
  isBenchCandidate?: boolean;
  isMlRecommendation: boolean;
  opportunityCandidate: OpportunityCandidateOrMlRecommended | undefined;
  opportunity: Opportunity;
};

export const OpportunityTalentContextMenu = ({
  name = 'OPPORTUNITY_TALENT_CANDIDATE_CONTEXT_MENU',
  candidate,
  currentCandidateStatus,
  isBenchCandidate = false,
  isMlRecommendation = false,
  opportunityCandidate,
  opportunity,
}: OpportunityTalentCandidateContextMenuProps) => {
  const navigate = useNavigate();
  const [{ data: positions }, { data: user }] = useApi(
    OpportunityLegacyApi.readOpportunityPositions(opportunity.id),
    UserApi.readSessionUser()
  );
  const statusOptionsDisabled =
    opportunity.selfService && !opportunity.isSynced;

  const talentPosition = positions.find(position =>
    position.candidates.find(c => c.candidateId === candidate.id)
  );

  const { interviewCandidate } = useInterviewAvailabilityCandidateData({
    candidateRedux: candidate,
  });

  const isAnotherCandidateSelected = (position?: OpportunityPosition) => {
    return OpportunityTalentMenuUtil.isAnotherCandidateSelected({
      position,
      candidateId: candidate.id,
    });
  };

  // While another candidate is selected for the position, show possible status transitions available once the other
  // candidate is moved out of selected
  const previewStatusOptions =
    (currentCandidateStatus && isAnotherCandidateSelected(talentPosition)
      ? PREVIEW_TRANSITIONS[currentCandidateStatus]
      : []) || [];

  const candidateStatusOptions: Array<CandidateOpportunity['candidateStatus']> =
    candidateOpportunityStatusUtils.getAllowedTransitions({
      currentCandidateStatus,
      opportunity,
      position: talentPosition,
      role: undefined,
    });

  const getCannotChangeStatusCopy = () => {
    return OpportunityTalentMenuUtil.getCannotChangeStatusCopy({
      isAnotherCandidateSelected: isAnotherCandidateSelected(talentPosition),
      opportunity,
    });
  };

  const openTableau = () => {
    const url = `${EnvUtil.tableauSimilarTalentUrl}?${qs.stringify({
      TNP: candidate.calculatedDisplayName,
    })}`;
    window.open(url, '_blank');
  };

  const handleRefreshTalent = async (candidateId: string) => {
    await CandidateApi.refreshCandidate({ candidateId });
  };

  const setFlagged = async () => {
    await CandidateApi.updateCandidate(candidate.id, {
      isFlagged: !candidate.isFlagged,
    });
    await handleRefreshTalent(candidate.id);
  };

  const [showReassignConfirmModal, setShowReassignConfirmModal] =
    useState(false);
  const [reassignPositionId, setReassignPositionId] = useState(
    talentPosition?.id
  );
  const [reassignedCandidateId, setReassignedCandidateId] = useState<
    string | null
  >(null);

  const [showWarmingModal, setShowWarmingModal] = useState(false);

  const [autoCoolingCandidateId, setAutoCoolingCandidateId] =
    useState<string>();

  const [
    showEditInterviewAvailabilityModal,
    setShowEditInterviewAvailabilityModal,
  ] = useState<TalentInterviewAvailabilityModalProps['defaultMode'] | null>(
    null
  );

  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectCandidateStatus, setRejectCandidateStatus] = useState<
    CandidateOpportunity['candidateStatus'] | null
  >(null);

  useEffect(() => {
    if (reassignedCandidateId && opportunityCandidate) {
      AddTalentUtil.handleWarnings([opportunityCandidate], candidate.id);
      setReassignedCandidateId(null);
    }
  }, [reassignedCandidateId]);

  const refreshAPIs = async () => {
    await Promise.all([
      OpportunityLegacyApi.refreshOpportunityCandidates(opportunity.id),
      OpportunityLegacyApi.refreshOpportunityPositions(opportunity.id),
      OpportunityStore.load(opportunity.id),
      CandidateOpportunityLegacyApi.refreshCandidateOpportunities(candidate.id),
    ]);
  };

  const updateOpportunityCandidateAndCost = async (
    statusOption: OpportunityCandidate['candidateStatus']
  ) => {
    try {
      await OpportunityLegacyApi.updateOpportunityCandidate(
        opportunity.id,
        candidate.id,
        {
          candidateStatus: statusOption,
          // Only include isActiveForSubmission when status is negative
          ...(SubmissionCandidateNegativeStatuses.includes(statusOption) && {
            isActiveForSubmission: false,
          }),
          ...(statusOption === CandidateStatuses.Selected && {
            isPriorityTalentAtSelected: candidate.isPriorityTalent,
          }),
        }
      );

      if (talentPosition) {
        await OpportunityLegacyApi.updateBurdendedCost(
          opportunity.id,
          candidate.id
        );
      }
    } catch (e) {
      PreloadedAppErrorsStore.show(
        ((e as ApiError).getErrors() as { message: string } | undefined)
          ?.message
      );
    }
    await refreshAPIs();
  };

  const handleReassignConfirmModalClose = (
    candidateId: Candidate['id'] | null
  ) => {
    setShowReassignConfirmModal(false);
    setReassignedCandidateId(candidateId);
  };

  const handleReassignPosition = async (positionId: string) => {
    setReassignPositionId(positionId);

    if (talentPosition) {
      setShowReassignConfirmModal(true);
    } else if (isBenchCandidate) {
      try {
        await OpportunityLegacyApi.createOpportunityCandidates(opportunity.id, {
          candidateIds: [candidate.id],
          positionId,
        });
        await OpportunityLegacyApi.updateBurdendedCost(
          opportunity.id,
          candidate.id
        );
      } catch (e) {
        PreloadedAppErrorsStore.show(
          ((e as ApiError).getErrors() as { message: string } | undefined)
            ?.message
        );
      }
      await Promise.all([
        OpportunityLegacyApi.refreshOpportunityCandidates(opportunity.id),
        OpportunityLegacyApi.refreshOpportunityPositions(opportunity.id),
        OpportunityStore.load(opportunity.id),
        CandidateOpportunityLegacyApi.refreshCandidateOpportunities(
          candidate.id
        ),
        AccountBenchApi.refreshOpportunityBench({
          opportunityId: opportunity.id,
        }),
      ]);
      handleReassignConfirmModalClose(candidate.id);
    } else {
      await OpportunityTalentUtil.reassignPosition({
        opportunityId: opportunity.id,
        candidateId: candidate.id,
        positionId,
        isMlRecommendation,
      });
      handleReassignConfirmModalClose(candidate.id);
    }
  };

  const copyLink = async () => {
    await ClipboardUtil.copy({
      text: AxiomForTalentUtil.getAftWarmingModalLink(opportunity.id),
    });

    ToastUtil.add({
      name: 'COPY_AFT_WARMING_LINK',
      type: 'info',
      children: <CondensedLarge>Talent warming link copied!</CondensedLarge>,
    });
  };

  const positionCopy = (position: OpportunityPosition, index: number) => {
    const copyFn = isBenchCandidate
      ? OpportunityTalentMenuUtil.getAddPositionCopy
      : OpportunityTalentMenuUtil.getMovePositionCopy;
    return copyFn({
      position,
      index,
      opportunity,
      isAnotherCandidateSelected: isAnotherCandidateSelected(position),
    });
  };

  const navigateToSubmissionTab = (candidateId: string, tab?: string) => {
    const path = tab
      ? `../../submission/${candidateId}/${tab}`
      : `../../submission/${candidateId}`;
    void navigate(path);
  };

  const loadAutoCoolingModal = (
    candidateId: string,
    opportunityId: string,
    candidateStatus: CandidateOpportunity['candidateStatus']
  ) => {
    FormAutoCoolingModalStore.load({
      candidateId,
      opportunityId,
      candidateStatus,
    });
  };

  const handleSubmitClick = () => {
    navigateToSubmissionTab(candidate.id);
  };

  const handleCooledClick = () => {
    setAutoCoolingCandidateId(candidate.id);
    loadAutoCoolingModal(
      candidate.id,
      opportunity.id,
      CandidateStatuses.Cooled
    );
  };

  const handleInterestedRejectedClick = () => {
    setRejectCandidateStatus(CandidateStatuses.InterestedRejected);
    setShowRejectionModal(true);
  };

  const openEventModal = async (
    subject: typeof EventSubjectConst.Opportunity,
    options: {
      candidateId: string;
      opportunityId: string;
      type?: string;
      purpose?: string;
    }
  ) => {
    await AppEventItemModalStore.openModal(subject, options);
  };

  const handleInterviewingClick = async (
    statusOption: CandidateOpportunity['candidateStatus']
  ) => {
    if (!opportunity.isQualificationComplete) {
      PreloadedAppErrorsStore.show(
        'Please qualify this opportunity before setting talent status to Interview.'
      );
      return;
    }

    await openEventModal(EventSubjectConst.Opportunity, {
      candidateId: candidate.id,
      opportunityId: opportunity.id,
      type: EventsConst.Types.Interview,
      purpose: EventsConst.Purposes.OpportunityInterview,
    });
    await updateOpportunityCandidateAndCost(statusOption);
  };

  const handleWarmedClick = () => {
    setShowWarmingModal(true);
  };

  const showAvailabilityOutdatedToast = () => {
    ToastUtil.add({
      children: (
        <>
          <CondensedHeader name="TALENT_INTERVIEW_AVAILABILITY_OUTDATED_TOAST_HEADER">
            {candidate.calculatedDisplayName} does not have enough interview
            availability
          </CondensedHeader>
          <CondensedLarge name="TALENT_INTERVIEW_AVAILABILITY_OUTDATED_TOAST_BODY">
            Ask {candidate.calculatedDisplayName} to update their availability
            or do it on their behalf.
          </CondensedLarge>
        </>
      ),
      dismissible: true,
      name: 'TALENT_INTERVIEW_AVAILABILITY_OUTDATED_TOAST',
      type: 'critical',
    });
  };

  const showOptInInformationToast = () => {
    ToastUtil.add({
      name: 'OPT_IN_INFORMATION_NEEDED',
      dismissible: true,
      type: 'critical',
      children: (
        <>
          <CondensedHeader>Opt-in information needed</CondensedHeader>
          <Gutter bottom="4px" />
          <CondensedLarge>
            Please remind {candidate.calculatedFirstName} to fill out the Opt In
            questions.{' '}
            <a
              data-test="AFT_WARMING_LINK"
              onClick={() => {
                void copyLink();
              }}
            >
              Copy link
            </a>
          </CondensedLarge>
          <br />
          <CondensedLarge>
            Talent Opt In Detail will display in activity log when talent
            provides answers.
          </CondensedLarge>
        </>
      ),
    });
  };

  const handleWarmedYesClick = async (
    statusOption: CandidateOpportunity['candidateStatus']
  ) => {
    await updateOpportunityCandidateAndCost(statusOption);

    if (candidate.isInterviewAvailabilityOutdated) {
      showAvailabilityOutdatedToast();
    }

    showOptInInformationToast();
  };

  const handleMlRecommendationStatusClick = async (
    statusOption: CandidateOpportunity['candidateStatus']
  ) => {
    if (statusOption === MLRecommendationStatuses.AiRemoved) {
      setRejectCandidateStatus(statusOption);
      setShowRejectionModal(true);
    } else {
      try {
        await OpportunityLegacyApi.createOpportunityCandidates(opportunity.id, {
          candidateIds: [candidate.id],
          positionId: undefined,
        });
        if (statusOption === CandidateStatuses.ShortList) {
          await OpportunityLegacyApi.updateOpportunityCandidate(
            opportunity.id,
            candidate.id,
            {
              candidateStatus: statusOption,
            }
          );
        }
      } catch (e) {
        PreloadedAppErrorsStore.show(
          ((e as ApiError).getErrors() as { message: string } | undefined)
            ?.message
        );
      }
      await refreshAPIs();
    }
  };

  const handleShowAvailabilityCalculation = () => {
    void CandidateAvailabilityModalStore.openModal(candidate.id);
  };

  return (
    <div data-test={name}>
      <ContextMenu
        name="CANDIDATE_CONTEXT_MENU"
        direction="left"
        anchor={
          <IconButton
            icon="vertical-ellipsis"
            variation="minimal"
            pattern="secondary"
            inverse
          />
        }
      >
        <ContextMenuHeader name="UPDATE_STATUS_HEADER">
          Update Status
        </ContextMenuHeader>
        {candidateStatusOptions.length === 0 &&
        previewStatusOptions.length === 0 ? (
          <ContextMenuItem disabled name="CANNOT_CHANGE_STATUS">
            {getCannotChangeStatusCopy()}
          </ContextMenuItem>
        ) : (
          <>
            {previewStatusOptions.map(
              statusOption =>
                statusOption && (
                  <ContextMenuItem
                    name={`preview-status-${statusOption.toLowerCase()}`}
                    key={`preview-status-${statusOption.toLowerCase()}`}
                    disabled
                  >
                    {positionContextMenuStatusLabels[statusOption]}
                  </ContextMenuItem>
                )
            )}
          </>
        )}
        {(candidateStatusOptions.length > 0 ||
          currentCandidateStatus === CandidateStatuses.WarmedYes) && (
          <>
            {currentCandidateStatus === CandidateStatuses.WarmedYes && (
              <ContextMenuItem
                disabled={statusOptionsDisabled}
                name="SUBMIT_ITEM"
                onClick={handleSubmitClick}
              >
                Submit
              </ContextMenuItem>
            )}
            {candidateStatusOptions.map(statusOption => (
              <React.Fragment key={`update-status-${statusOption}-key`}>
                {statusOption === CandidateStatuses.Cooled &&
                  !isMlRecommendation && (
                    <ContextMenuItem
                      disabled={statusOptionsDisabled}
                      onClick={handleCooledClick}
                      name="update-status-cooled"
                    >
                      {positionContextMenuStatusLabels[statusOption]}
                    </ContextMenuItem>
                  )}
                {statusOption === CandidateStatuses.InterestedRejected &&
                !isMlRecommendation ? (
                  <ContextMenuItem
                    disabled={statusOptionsDisabled}
                    onClick={handleInterestedRejectedClick}
                    name="update-status-interestedrejected-specific"
                  >
                    {poolContextMenuStatusLabels[statusOption]}
                  </ContextMenuItem>
                ) : null}
                {statusOption === CandidateStatuses.Interviewing &&
                  !isMlRecommendation && (
                    <ContextMenuItem
                      disabled={statusOptionsDisabled}
                      onClick={async () =>
                        handleInterviewingClick(statusOption)
                      }
                      name="update-status-interviewing-specific"
                    >
                      {positionContextMenuStatusLabels[statusOption]}
                    </ContextMenuItem>
                  )}
                {statusOption === CandidateStatuses.Selected &&
                !isMlRecommendation ? (
                  <ContextMenuItem
                    onClick={async () => {
                      await updateOpportunityCandidateAndCost(statusOption);
                    }}
                    name="update-status-selected-specific"
                    disabled={
                      statusOptionsDisabled ||
                      !CandidateOpportunityUtils.hasSubmittedRate(
                        opportunityCandidate
                      ) ||
                      CandidateOpportunityUtils.baseHourlyUnavailable(
                        opportunityCandidate
                      )
                    }
                  >
                    {positionContextMenuStatusLabels[statusOption]}
                  </ContextMenuItem>
                ) : null}
                {statusOption === CandidateStatuses.Warmed &&
                  !isMlRecommendation && (
                    <ContextMenuItem
                      disabled={statusOptionsDisabled}
                      onClick={handleWarmedClick}
                      name="update-status-warmed-specific"
                    >
                      {positionContextMenuStatusLabels[statusOption]}
                    </ContextMenuItem>
                  )}
                {statusOption === CandidateStatuses.WarmedYes &&
                  !isMlRecommendation && (
                    <ContextMenuItem
                      disabled={statusOptionsDisabled}
                      onClick={async () => handleWarmedYesClick(statusOption)}
                      name="update-status-warmedyes-specific"
                    >
                      {positionContextMenuStatusLabels[statusOption]}
                    </ContextMenuItem>
                  )}
                {[
                  CandidateStatuses.Rejected,
                  CandidateStatuses.Removed,
                  CandidateStatuses.RepBioShare,
                  CandidateStatuses.ShortList,
                  CandidateStatuses.Suggested,
                  CandidateStatuses.TalentOptOut,
                ].includes(statusOption) &&
                !isMlRecommendation &&
                statusOption ? (
                  <ContextMenuItem
                    disabled={statusOptionsDisabled}
                    onClick={async () => {
                      await updateOpportunityCandidateAndCost(statusOption);
                    }}
                    name={`update-status-${statusOption.toLowerCase()}-specific`}
                  >
                    {positionContextMenuStatusLabels[statusOption]}
                  </ContextMenuItem>
                ) : null}
                {isMlRecommendation && statusOption ? (
                  <ContextMenuItem
                    disabled={statusOptionsDisabled}
                    onClick={async () =>
                      handleMlRecommendationStatusClick(statusOption)
                    }
                    name={`update-status-${statusOption.toLowerCase()}`}
                  >
                    {statusOption === MLRecommendationStatuses.AiRemoved
                      ? poolContextMenuStatusLabels[statusOption]
                      : positionContextMenuStatusLabels[statusOption]}
                  </ContextMenuItem>
                ) : null}
              </React.Fragment>
            ))}
          </>
        )}
        <ContextMenuDivider name="MOVE_POSITION_DIVIDER" />
        <ContextMenuHeader name="MOVE_POSITION_HEADER">
          {isBenchCandidate ? 'Add to Position' : 'Move Position'}
        </ContextMenuHeader>
        {positions.length === 0 ||
        (talentPosition && positions.length === 1) ||
        opportunity.isClosed ||
        !opportunity.isFulfillmentActive ? (
          <ContextMenuItem disabled name="CANT_CHANGE_POSITION_ITEM">
            Can't change position
          </ContextMenuItem>
        ) : (
          <>
            {PositionUtil.sortPositions(positions)
              .map((position, originalIndex) => ({ position, originalIndex }))
              .filter(({ position }) => position.id !== talentPosition?.id)
              .map(({ position, originalIndex }, filteredIndex) => (
                <ContextMenuItem
                  key={`position-${position.id}-menu-item`}
                  disabled={
                    isAnotherCandidateSelected(position) ||
                    (opportunity.selfService && !opportunity.isSynced)
                  }
                  name={`MOVE_TO_POSITION_ITEM_${filteredIndex + 1}`}
                  onClick={async () => handleReassignPosition(position.id)}
                >
                  {positionCopy(position, originalIndex)}
                </ContextMenuItem>
              ))}
          </>
        )}
        <ContextMenuDivider name="ADDITIONAL_SETTINGS_DIVIDER" />
        <ContextMenuHeader name="ADDITIONAL_SETTINGS_HEADER">
          Additional Settings
        </ContextMenuHeader>
        {!!talentPosition && (
          <ContextMenuItem
            name="CANDIDATE_CONTEXT_SET_RATE"
            onClick={() => {
              navigateToSubmissionTab(candidate.id, 'rate');
            }}
            disabled={
              currentCandidateStatus === CandidateStatuses.Removed ||
              currentCandidateStatus === CandidateStatuses.TalentOptOut ||
              (opportunity.selfService && !opportunity.isSynced)
            }
          >
            Set rate
          </ContextMenuItem>
        )}
        <ContextMenuItem
          onClick={async () => {
            await openEventModal(EventSubjectConst.Candidate, {
              candidateId: candidate.id,
              opportunityId: opportunity.id,
            });
          }}
          name="CANDIDATE_CONTEXT_MENU_ADD_ACTIVITY"
        >
          Add activity
        </ContextMenuItem>
        <ContextMenuItem
          name="OPEN_BIO"
          onClick={() => {
            AxiomForTalentUtil.openBio(candidate.id);
          }}
        >
          Open bio
        </ContextMenuItem>
        {AxiomForTalentUtil.canViewAft(candidate, user) && (
          <ContextMenuItem
            name="CANDIDATE_CONTEXT_MENU_OPEN_AFT"
            onClick={() => {
              AxiomForTalentUtil.openAftProfile(candidate.id);
            }}
          >
            Open in Axiom for Talent
          </ContextMenuItem>
        )}
        <ContextMenuItem
          onClick={handleShowAvailabilityCalculation}
          name="CANDIDATE_CONTEXT_MENU_AVAILABILITY"
        >
          Show availability calculation
        </ContextMenuItem>
        <ContextMenuItem
          onClick={openTableau}
          name="CANDIDATE_CONTEXT_MENU_TABLEAU"
        >
          Find similar talent in Tableau
        </ContextMenuItem>
        {!isSalesUser(user) && (
          <ContextMenuItem
            onClick={setFlagged}
            name="CANDIDATE_CONTEXT_MENU_FLAG"
          >
            {candidate.isFlagged ? 'Remove flag' : 'Flag profile'}
          </ContextMenuItem>
        )}
        <ContextMenuDivider name="AVAILABILITY_SETTINGS_DIVIDER" />
        <CandidateCalendarMenuContent
          user={user}
          onEditAvailability={setShowEditInterviewAvailabilityModal}
          opportunityCandidate={opportunityCandidate}
        >
          <ContextMenuHeader name="TALENT_AVAILABILITY_HEADER">
            Availability Settings
          </ContextMenuHeader>
        </CandidateCalendarMenuContent>
      </ContextMenu>
      {showReassignConfirmModal ? (
        <OpportunityTalentReassignPositionModal
          onClose={handleReassignConfirmModalClose}
          isMlRecommendation={isMlRecommendation}
          opportunityId={opportunity.id}
          candidateId={candidate.id}
          positionId={reassignPositionId}
        />
      ) : null}
      {showWarmingModal ? (
        <OpportunityTalentWarmingModal
          onClose={() => {
            setShowWarmingModal(false);
          }}
          opportunity={opportunity}
          opportunityCandidate={opportunityCandidate}
          candidate={candidate}
        />
      ) : null}
      {showEditInterviewAvailabilityModal && interviewCandidate ? (
        <TalentInterviewAvailabilityModal
          candidate={interviewCandidate}
          user={user}
          defaultMode={showEditInterviewAvailabilityModal}
          onClose={() => {
            setShowEditInterviewAvailabilityModal(null);
          }}
          opportunityCandidateStatus={opportunityCandidate?.candidateStatus}
        />
      ) : null}
      {showRejectionModal ? (
        <RejectionModal
          candidateId={candidate.id}
          opportunityId={opportunity.id}
          candidateStatus={rejectCandidateStatus}
          onClose={() => {
            setRejectCandidateStatus(null);
            setShowRejectionModal(false);
          }}
        />
      ) : null}
      <FormStateTreatment
        name="COOL_MODAL_TREATMENT"
        isModalState
        dataId={autoCoolingCandidateId}
        dataIdName="candidateId"
        formStore={FormAutoCoolingModalStore}
        renderLoadedView={({ fireSubmit, fireCancel, formData }) => {
          return (
            <AutoCoolingModal
              form={formData}
              onCancel={() => {
                fireCancel();
                setAutoCoolingCandidateId(undefined);
              }}
              onSend={() => {
                fireSubmit({ isActiveForSubmission: false });
                setAutoCoolingCandidateId(undefined);
              }}
            />
          );
        }}
      />
    </div>
  );
};
